// Development page — player drawer: per block, all 7 metrics as bars of ratio vs the reference
// group (dashed line = group median, shaded = ±10%), the reference group by name, the numbers
// behind each bar and the full sentences. Full screen below ~900px.
import React, { useEffect, useMemo, useRef } from 'react';
import { X } from 'lucide-react';
import {
  METRICS, BLOCKS, BLOCK_LABEL, LONG_LABEL, NOT_PICKABLE_NOTE, RatioBar, RatioAxis, ratioDomain,
  referenceText, fmtPct, fmtValue,
} from './developmentShared';

const cellTh = { textAlign: 'right', padding: '4px 6px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
const cellTd = { textAlign: 'right', padding: '4px 6px', fontSize: 12, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };

// What a metric row is, in words: the picked key point, or why it can't be one.
function rowTag(blockKey, block, metricKey) {
  const kp = block.keyPoints;
  if (kp.specialisation && kp.specialisation.metric === metricKey) return { text: 'Specialisation', strong: true };
  if (kp.improvement && kp.improvement.metric === metricKey) return { text: 'Area to improve', strong: true };
  if ((block.notPickable || []).includes(metricKey)) return { text: 'not pickable', title: `${BLOCK_LABEL[blockKey]} ${metricKey.toUpperCase()}: ${NOT_PICKABLE_NOTE}` };
  if (block.metrics[metricKey].tooFewPoints && block.state === 'ok') return { text: 'too few points', title: `Only ${block.metrics[metricKey].n} valid ${blockKey === 'match' ? 'matches' : 'weeks'} for this metric` };
  return null;
}

function BlockSection({ blockKey, block, domain }) {
  const unit = blockKey === 'match' ? 'matches' : 'weeks';
  const usable = block.state === 'ok' && !block.keyPoints.state;
  const ref = referenceText(block);
  return (
    <section style={{ marginTop: 14, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{BLOCK_LABEL[blockKey]}</h4>
        <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
          {blockKey === 'match' ? 'mean per 90 over competitive matches of 60+ min' : 'mean of weekly total ÷ squad median'}
          {' · '}n = {block.nValid} {unit}
        </span>
      </div>
      {ref && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
          {ref}
          {block.referenceUsed !== 'position' && <span style={{ color: 'var(--text-muted)' }}> — position group too small</span>}
        </div>
      )}
      {!usable ? (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8 }}>{block.keyPoints.state?.text}</div>
      ) : (
        <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'minmax(46px, auto) 1fr 52px minmax(96px, auto)', columnGap: 10, rowGap: 4, alignItems: 'center' }}>
          <span /><RatioAxis domain={domain} /><span /><span />
          {METRICS.map(m => {
            const mm = block.metrics[m.key];
            const tag = rowTag(blockKey, block, m.key);
            const title = `${LONG_LABEL[m.key]}: ${fmtValue(blockKey, m.key, mm.value)} vs group median ${fmtValue(blockKey, m.key, mm.groupMedian)} (${fmtPct(mm.pctVsGroup)}, n = ${mm.n})`;
            return (
              <React.Fragment key={m.key}>
                <span style={{ fontSize: 12, fontWeight: tag?.strong ? 700 : 500, color: 'var(--text-primary)' }}>{m.label}</span>
                <RatioBar ratio={mm.ratio} domain={domain} title={title} />
                <span style={{ fontSize: 12, fontWeight: 600, textAlign: 'right', color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{fmtPct(mm.pctVsGroup)}</span>
                <span title={tag?.title} style={{ fontSize: 11, color: tag?.strong ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: tag?.strong ? 700 : 500, whiteSpace: 'nowrap' }}>
                  {tag?.text || ''}
                </span>
              </React.Fragment>
            );
          })}
        </div>
      )}

      <div style={{ overflowX: 'auto', marginTop: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...cellTh, textAlign: 'left' }}>Metric</th>
              <th style={cellTh}>{blockKey === 'match' ? 'Value /90' : 'Value'}</th>
              <th style={cellTh}>Group median</th>
              <th style={cellTh}>vs group</th>
              <th style={cellTh}>n</th>
            </tr>
          </thead>
          <tbody>
            {METRICS.map(m => {
              const mm = block.metrics[m.key];
              return (
                <tr key={m.key}>
                  <td style={{ ...cellTd, textAlign: 'left' }} title={LONG_LABEL[m.key]}>{m.label}</td>
                  <td style={cellTd}>{fmtValue(blockKey, m.key, mm.value)}</td>
                  <td style={cellTd}>{fmtValue(blockKey, m.key, mm.groupMedian)}</td>
                  <td style={cellTd}>{fmtPct(mm.pctVsGroup)}</td>
                  <td style={{ ...cellTd, color: mm.tooFewPoints ? 'var(--text-muted)' : cellTd.color }}>{mm.n ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {block.groupSize != null && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
            Group size {block.groupSize} (incl. this player) · group median = median of the other {block.groupSize - 1} players
          </div>
        )}
      </div>
    </section>
  );
}

export default function DevelopmentPlayerDrawer({ player, fullScreen, onClose }) {
  const closeRef = useRef(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const domain = useMemo(() => ratioDomain(BLOCKS.flatMap(b => METRICS.map(m => player[b].metrics[m.key].ratio))), [player]);
  const squadPos = player.positionGroup !== player.position ? ` (squad position ${player.position})` : '';

  return (
    <div role="presentation" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <aside role="dialog" aria-modal="true" aria-label={`Development — ${player.name}`} onClick={e => e.stopPropagation()}
        style={{ width: fullScreen ? '100vw' : 'min(720px, 100vw)', height: '100%', overflowY: 'auto', background: 'var(--bg-app)',
          borderLeft: fullScreen ? 'none' : '1px solid var(--border-strong)', padding: fullScreen ? 16 : 20, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)' }}>{player.name}</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
              {player.positionGroup}{squadPos} · {player.minutesInWindow} competitive minutes in the period
            </div>
          </div>
          <button ref={closeRef} onClick={onClose} aria-label="Close"
            style={{ border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', borderRadius: 8, padding: 6, cursor: 'pointer' }}>
            <X size={16} />
          </button>
        </div>

        <section style={{ marginTop: 16, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted)', marginBottom: 6 }}>Key points</div>
          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-primary)', fontSize: 13, lineHeight: 1.5 }}>
            {player.sentences.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
            Suggestions with the numbers behind them. A value below the group is not necessarily a weakness — role and tactics matter.
          </div>
        </section>

        {BLOCKS.map(b => <BlockSection key={b} blockKey={b} block={player[b]} domain={domain} />)}

        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12 }}>
          Bars: player vs the median of the other players in his reference group. Dashed line = group median, shaded band = ±10%
          (inside it a metric is never a key point). Blue = above the group, orange = below — direction only, not good or bad.
          Training SD is shown but never picked; a metric with fewer valid points than the block minimum is shown but skipped.
        </p>
      </aside>
    </div>
  );
}
